<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在准备编辑器">
    <template #header-left>
      <div class="editor-head">
        <i class="el-icon-arrow-left" @click="goBack"></i>
        <div>
          <b>{{ isRoleCard ? "创建 / 编辑 角色卡" : "创建 / 编辑 伴侣" }}</b>
          <small>{{ isRoleCard ? "一张角色卡可以创建多个伴侣" : "伴侣是用角色卡创建的、能聊天的对象" }}</small>
        </div>
      </div>
    </template>
    <div class="editor-page">
      <div class="concept-bar" :class="{ companion: !isRoleCard }">
        <i :class="isRoleCard ? 'el-icon-postcard' : 'el-icon-user-solid'"></i>
        <p v-if="isRoleCard">
          你正在编辑 <b>角色卡</b>：一份可复用的角色设定。保存后能用它创建<b>多个伴侣</b>。
        </p>
        <p v-else>
          你正在创建 <b>伴侣</b>：先选一张<b>角色卡</b>，再补充这个伴侣专属的资料、开场白和微信绑定。
        </p>
      </div>

      <div class="top-actions">
        <el-button round @click="goBack">{{ readonly ? "返回" : "取消" }}</el-button>
        <el-button v-if="!readonly" round @click="saveDraft">存为草稿</el-button>
        <el-button v-if="!readonly" type="primary" round :loading="saving" @click="onPublish">
          {{ isRoleCard ? "保存角色卡" : "创建伴侣" }}
        </el-button>
        <span v-if="readonly" class="readonly-tip">系统角色卡，仅可查看</span>
      </div>

      <div class="layout">
        <div class="form-col">
          <!-- 基本信息 -->
          <div class="section">
            <h3><span>1</span>基本信息</h3>
            <el-form label-position="top" :disabled="readonly">
              <div class="grid-2">
                <el-form-item label="名称">
                  <el-input v-model="form.name" :placeholder="isRoleCard ? '角色卡名称，如：温柔学姐 小麦' : '伴侣名称'" />
                </el-form-item>
                <el-form-item label="一句话介绍">
                  <el-input v-model="form.description" placeholder="一句话描述这个角色" />
                </el-form-item>
              </div>
            </el-form>
          </div>

          <!-- 角色卡模式：外貌描写 -->
          <div v-if="isRoleCard" class="section">
            <h3><span>2</span>外貌描写</h3>
            <el-form label-position="top" :disabled="readonly">
              <el-form-item label="外貌总述（脸型 / 发型 / 身材 / 穿衣风格 / 气质）">
                <el-input type="textarea" :rows="3" v-model="rc.appearance" placeholder="例如：鹅蛋脸，黑色长直发，清瘦，常穿米色针织衫，气质温柔安静" />
              </el-form-item>
              <el-form-item label="生图参考规则（正向，逗号分隔）">
                <el-input v-model="rc.refRules" placeholder="保持黑长直、暖色调、自然光" />
              </el-form-item>
              <el-form-item label="生图负面规则（避免，逗号分隔）">
                <el-input v-model="rc.negRules" placeholder="不要浓妆、不要夸张表情、不要背景杂乱" />
              </el-form-item>
            </el-form>
          </div>

          <!-- 角色卡模式：人格 Prompt -->
          <div v-if="isRoleCard" class="section">
            <h3><span>3</span>人格 Prompt 维度</h3>
            <div class="mode-switch-row">
              <el-radio-group v-model="rcMode" size="small">
                <el-radio-button label="simple">简易模式</el-radio-button>
                <el-radio-button label="pro">专业模式</el-radio-button>
              </el-radio-group>
              <span class="mode-tip">{{ rcMode === "simple" ? "回答几个问题，AI 帮你扩写成完整人设" : "直接编辑 10 个人设维度" }}</span>
            </div>
            <el-form label-position="top" :disabled="readonly">
              <el-form-item label="性格标签（逗号分隔）">
                <el-input v-model="rc.tagsText" placeholder="温柔, 治愈, 学姐" />
              </el-form-item>
              <el-form-item label="推荐关系定位（创建伴侣时默认带入，用户可改）">
                <el-select v-model="rc.relationshipMode" style="width: 100%">
                  <el-option label="朋友 / 损友（可发展深度信任）" value="friend" />
                  <el-option label="倾听者 / 树洞（温暖陪伴，不恋爱）" value="listener" />
                  <el-option label="导师 / 前辈（友善但有距离）" value="mentor" />
                  <el-option label="恋人（可慢慢走向亲密恋爱）" value="romantic" />
                </el-select>
              </el-form-item>
              <el-form-item label="推荐吸引力风格（创建伴侣时默认带入，用户可改）">
                <el-select v-model="rc.attractionStyle" style="width: 100%">
                  <el-option label="被在意 · 被记得（暖心陪伴）" value="caring" />
                  <el-option label="心动 · 情感张力（仅在关系允许时）" value="romance" />
                  <el-option label="有趣 · 欢乐损友（轻松解压）" value="fun" />
                </el-select>
              </el-form-item>
              <el-form-item label="推荐互动风格（拟真↔情绪价值，用户可改）">
                <el-select v-model="rc.interactionMode" style="width: 100%">
                  <el-option label="混合型（有真人质感，但终点让人安心）" value="hybrid" />
                  <el-option label="情绪导向型（始终温暖在场，主打情绪价值）" value="emotional" />
                  <el-option label="真人模拟型（有脾气有节奏，更像真人）" value="realistic" />
                </el-select>
              </el-form-item>
              <el-form-item label="家乡城市（新建伴侣时默认带入，用户可改）">
                <el-select v-model="rc.location" filterable allow-create default-first-option placeholder="选择或输入城市" style="width: 100%">
                  <el-option v-for="c in cityOptions" :key="c" :label="c" :value="c" />
                </el-select>
              </el-form-item>
              <template v-if="rcMode === 'simple'">
                <el-form-item label="她是谁（身份 / 背景）">
                  <el-input type="textarea" :rows="2" v-model="simple.who" placeholder="例如：22岁美术学院大四学生，在准备毕业展，住学校宿舍" />
                </el-form-item>
                <el-form-item label="和你的关系">
                  <el-input v-model="simple.relationship" placeholder="例如：认识两个月的暧昧对象，还没确定关系" />
                </el-form-item>
                <el-form-item label="她怎么说话（语气 / 口头禅）">
                  <el-input type="textarea" :rows="2" v-model="simple.style" placeholder="例如：短句多，爱用「诶」开头，被夸会嘴硬，从不发长篇大论" />
                </el-form-item>
                <el-form-item label="对话示例（可选，每行一组：我说xx → 她回xx）">
                  <el-input type="textarea" :rows="3" v-model="simple.examples" placeholder="我：在干嘛 → 她：刚画完一张图，手要废了&#10;我：想你了 → 她：才不信，你昨天也这么说" />
                </el-form-item>
                <el-form-item label="雷点 / 禁忌（可选）">
                  <el-input v-model="simple.taboos" placeholder="例如：不聊前任，不接受被叫宝贝" />
                </el-form-item>
                <el-button type="primary" round size="small" :loading="expanding" icon="el-icon-magic-stick" @click="runExpandPersona">
                  {{ expanding ? "AI 正在扩写…" : "AI 生成完整人设" }}
                </el-button>
                <p class="mode-tip">生成后会自动填入专业模式的各个维度，可切到专业模式微调。</p>
              </template>
              <template v-else>
                <el-form-item v-for="item in promptSectionDefs" :key="item.key" :label="item.label">
                  <el-input type="textarea" :rows="item.rows || 2" v-model="rc.promptSections[item.key]" :placeholder="item.placeholder" />
                </el-form-item>
              </template>
            </el-form>
          </div>

          <!-- 角色卡模式：角色照片（放最后，可稍后补） -->
          <div v-if="isRoleCard" class="section">
            <h3><span>4</span>角色照片<small class="step-optional">选填</small></h3>
            <div class="photo-block">
              <div class="photo-preview">
                <img v-if="rc.photo" :src="rc.photo" alt="" />
                <div v-else class="photo-empty"><i class="el-icon-picture-outline"></i><span>暂无照片</span></div>
              </div>
              <div class="photo-actions">
                <el-upload action="#" :show-file-list="false" :before-upload="onUploadReference" :disabled="readonly">
                  <el-button size="small" round icon="el-icon-upload2" :disabled="readonly">上传参考图</el-button>
                </el-upload>
                <el-button size="small" round icon="el-icon-folder-opened" :disabled="readonly" @click="openLibrary">从图像库选择</el-button>
                <el-button size="small" round icon="el-icon-magic-stick" :disabled="readonly" @click="openGen('fast')">用提示词生成照片</el-button>
                <el-button size="small" round icon="el-icon-picture" :disabled="!canUseReferenceImage || readonly" @click="openGen('edit')">用参考图生成照片</el-button>
              </div>
              <p class="photo-tip">
                照片不是必填，保存后也能随时再生成或更换。参考图状态：{{ referenceStatusText }}，审核通过后才能用参考图生成照片。
              </p>
            </div>
          </div>

          <!-- 伴侣模式：选择角色卡 -->
          <div v-if="!isRoleCard" class="section">
            <h3><span>2</span>选择角色卡</h3>
            <div class="rc-picker">
              <div
                v-for="card in roleCards"
                :key="card.id"
                class="rc-option"
                :class="{ on: form.roleCardId === card.id }"
                @click="selectRoleCard(card)"
              >
                <img :src="card.cover || require('@/assert/主页人物.png')" alt="" />
                <div>
                  <b>{{ card.name }}</b>
                  <p>{{ card.desc }}</p>
                </div>
                <i class="el-icon-success" v-if="form.roleCardId === card.id"></i>
              </div>
            </div>
            <el-button size="small" round icon="el-icon-plus" class="new-rc" @click="goRoleCard">没有合适的？新建角色卡</el-button>
          </div>

          <!-- 伴侣模式：展示资料 -->
          <div v-if="!isRoleCard" class="section">
            <h3><span>3</span>展示资料</h3>
            <el-form label-position="top" :disabled="readonly">
              <div class="grid-2">
                <el-form-item label="年龄"><el-input-number v-model="cp.age" :min="18" :max="120" style="width: 100%" /></el-form-item>
                <el-form-item label="身份"><el-input v-model="cp.identity" placeholder="如：大学生 / 设计师" /></el-form-item>
                <el-form-item label="关系标签">
                  <el-select v-model="cp.relation" style="width: 100%">
                    <el-option v-for="r in relationOptions" :key="r" :label="r" :value="r" />
                  </el-select>
                </el-form-item>
                <el-form-item label="所在城市">
                  <el-select v-model="cp.city" filterable allow-create default-first-option placeholder="选择或输入城市" style="width: 100%">
                    <el-option v-for="c in cityOptions" :key="c" :label="c" :value="c" />
                  </el-select>
                </el-form-item>
                <el-form-item label="性格标签"><el-input v-model="cp.personality" placeholder="如：温柔体贴" /></el-form-item>
              </div>
              <el-form-item label="性格标签 chips">
                <span
                  v-for="t in traitChips"
                  :key="t"
                  class="chip"
                  :class="{ on: cp.traits.includes(t) }"
                  @click="toggleTrait(t)"
                >{{ t }}</span>
              </el-form-item>
              <el-form-item label="说话风格">
                <el-input type="textarea" :rows="2" v-model="cp.speakingStyle" placeholder="语气、口头禅、回复长短偏好" />
              </el-form-item>
            </el-form>
          </div>

          <!-- 伴侣模式：开场白 -->
          <div v-if="!isRoleCard" class="section">
            <h3><span>4</span>开场白</h3>
            <el-form label-position="top" :disabled="readonly">
              <el-form-item>
                <el-input type="textarea" :rows="2" v-model="cp.welcome" placeholder="你好呀，今天过得怎么样呢？" />
              </el-form-item>
              <div class="welcome-tpls">
                <span v-for="w in welcomeTpls" :key="w" @click="cp.welcome = w">{{ w }}</span>
              </div>
            </el-form>
          </div>

          <!-- 伴侣模式：高级设定 -->
          <div v-if="!isRoleCard" class="section">
            <h3><span>5</span>高级设定</h3>
            <el-form label-position="top" :disabled="readonly">
              <el-form-item label="自定义补充 Prompt">
                <el-input type="textarea" :rows="3" v-model="cp.customPrompt" placeholder="在角色卡 prompt 基础上，针对这个实例的额外设定" />
              </el-form-item>
              <div class="grid-2">
                <el-form-item label="生图模式">
                  <el-select v-model="cp.imageMode" style="width: 100%">
                    <el-option label="优先参考照片（更像本人）" value="edit_first" />
                    <el-option label="优先按描述画（更自由）" value="fast_first" />
                    <el-option label="只用参考照片" value="edit" />
                    <el-option label="只按文字描述画" value="fast" />
                  </el-select>
                </el-form-item>
                <el-form-item label="记忆带入条数（每次回看多少句历史）">
                  <el-input-number v-model="cp.historyLimit" :min="4" :max="50" style="width: 100%" />
                </el-form-item>
              </div>
              <el-form-item label="基础亲密度">
                <el-slider v-model="cp.intimacy" :max="100" />
              </el-form-item>
              <p class="mode-tip" style="margin-bottom: 10px">下面两项已按所选角色卡预填，可单独为这个伴侣微调。</p>
              <div class="grid-2">
                <el-form-item label="关系定位（决定关系走向与表达边界）">
                  <el-select v-model="cp.relationshipMode" style="width: 100%">
                    <el-option label="朋友 / 损友（可发展深度信任）" value="friend" />
                    <el-option label="倾听者 / 树洞（温暖陪伴，不恋爱）" value="listener" />
                    <el-option label="导师 / 前辈（友善但有距离）" value="mentor" />
                    <el-option label="恋人（可慢慢走向亲密恋爱）" value="romantic" />
                  </el-select>
                </el-form-item>
                <el-form-item label="吸引力风格（主打的相处感觉）">
                  <el-select v-model="cp.attractionStyle" style="width: 100%">
                    <el-option label="被在意 · 被记得（暖心陪伴）" value="caring" />
                    <el-option label="心动 · 情感张力（仅在关系允许时）" value="romance" />
                    <el-option label="有趣 · 欢乐损友（轻松解压）" value="fun" />
                  </el-select>
                </el-form-item>
                <el-form-item label="互动风格（拟真↔情绪价值）">
                  <el-select v-model="cp.interactionMode" style="width: 100%">
                    <el-option label="混合型（有真人质感，但终点让人安心）" value="hybrid" />
                    <el-option label="情绪导向型（始终温暖在场，主打情绪价值）" value="emotional" />
                    <el-option label="真人模拟型（有脾气有节奏，更像真人）" value="realistic" />
                  </el-select>
                </el-form-item>
                <el-form-item label="深度思考（开=更走心但更慢，关=回复更快）">
                  <el-switch v-model="cp.deepThinking" active-color="#c94225" />
                </el-form-item>
              </div>
            </el-form>
          </div>
        </div>

        <!-- 预览列 -->
        <div class="preview-col">
          <div class="preview-card">
            <h3>{{ isRoleCard ? "角色卡预览" : "伴侣预览" }}</h3>
            <img :src="previewPhoto" alt="" class="cover" />
            <h2>{{ form.name || (isRoleCard ? "未命名角色卡" : "未命名伴侣") }}</h2>
            <p>{{ previewSubtitle }}</p>
            <p class="bio">{{ form.description || "一句话介绍会显示在这里。" }}</p>
            <div v-if="isRoleCard && rc.appearance" class="appearance-preview">
              <label>外貌</label>
              <span>{{ rc.appearance }}</span>
            </div>
            <div v-if="!isRoleCard" class="bars">
              <div><label>基础亲密度</label><el-progress :percentage="cp.intimacy" color="#c94225" :show-text="false" /></div>
            </div>
          </div>
          <div v-if="isRoleCard" class="hint-card">
            <h3>怎么用这张卡</h3>
            <p>保存后，可在「角色卡」页点「用这张卡创建伴侣」，用它创建能聊天的<b>伴侣</b>，同一张卡可重复使用。</p>
          </div>
          <div v-else class="hint-card">
            <h3>下一步</h3>
            <p>点击「创建伴侣」后，会进入<b>微信号绑定</b>步骤：绑定已有微信号 / 扫码新增 / 暂不绑定。</p>
          </div>
        </div>
      </div>

      <div v-if="!readonly" class="bottom-actions">
        <span class="ba-hint">{{ isRoleCard ? "填好人设就能保存，照片可稍后再补" : "选好角色卡、起个名字就能创建" }}</span>
        <div class="ba-btns">
          <el-button round @click="saveDraft">存为草稿</el-button>
          <el-button type="primary" round :loading="saving" @click="onPublish">{{ isRoleCard ? "保存角色卡" : "创建伴侣" }}</el-button>
        </div>
      </div>
    </div>

    <!-- 生成角色照片 -->
    <el-dialog :title="genMode === 'fast' ? '用提示词生成角色照片' : '用参考图生成角色照片'" :visible.sync="genVisible" width="540px" custom-class="yw-dialog">
      <el-form label-position="top" :disabled="readonly">
        <el-form-item label="画面提示词（默认带入外貌描写）">
          <el-input type="textarea" :rows="3" v-model="genPrompt" />
        </el-form-item>
        <div class="grid-2">
          <el-form-item label="生成数量">
            <el-select v-model="genCount" style="width: 100%">
              <el-option v-for="n in [1,2,3,4]" :key="n" :label="n + ' 张'" :value="n" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="genMode === 'edit'" label="参考图">
            <el-input :value="canUseReferenceImage ? rc.referenceImage : '参考图未审核通过，暂不可用'" disabled />
          </el-form-item>
        </div>
      </el-form>
      <div v-if="genResults.length" class="gen-results">
        <div v-for="(g, i) in genResults" :key="i" class="gen-item" @click="useGenerated(g)">
          <img :src="g" alt="" />
          <span class="use">设为照片</span>
        </div>
      </div>
      <p class="gen-note"><i class="el-icon-info"></i> AI 生图约需 30–80 秒，请耐心等待。「用参考图」会更贴近本人，「用提示词」更自由发挥。</p>
      <span slot="footer">
        <el-button round @click="genVisible = false">关闭</el-button>
        <el-button type="primary" round :loading="generating" @click="runGenerate">生成</el-button>
      </span>
    </el-dialog>

    <!-- 从图像库选择参考图 -->
    <el-dialog title="从图像库选择参考图" :visible.sync="libVisible" width="640px" custom-class="yw-dialog">
      <p class="lib-tip">在「图像库」页生成 / 编辑 / 上传的图片都可以直接用作角色参考图（上传的图片仍需后台审核）。</p>
      <div v-if="libLoading" class="lib-empty"><i class="el-icon-loading"></i> 加载中…</div>
      <div v-else-if="!libAssets.length" class="lib-empty">图像库还是空的，先去「图像库」页生成或上传图片吧。</div>
      <div v-else class="lib-grid">
        <div v-for="asset in libAssets" :key="asset.id" class="lib-item" @click="pickFromLibrary(asset)">
          <img :src="asset.public_url" alt="" />
          <span class="pick">选用</span>
        </div>
      </div>
      <span slot="footer">
        <el-button round @click="libVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 发布伴侣：绑定微信号 -->
    <el-dialog title="伴侣已创建，绑定微信号" :visible.sync="publishVisible" width="480px" custom-class="yw-dialog" @close="stopBindPolling">
      <p class="pub-ok"><i class="el-icon-circle-check"></i> 「{{ form.name || "新伴侣" }}」创建成功！选择如何接管微信聊天：</p>
      <div class="bind-modes">
        <div class="mode" :class="{ on: bindMode === 'exist' }" @click="selectBindMode('exist')">
          <i class="el-icon-chat-round"></i>
          <div><b>绑定已有微信号</b><small>从你已绑定的微信号中选择一个</small></div>
        </div>
        <div v-if="bindMode === 'exist'" class="select-wrap">
          <el-select v-model="chosenWx" placeholder="选择微信号" style="width: 100%">
            <el-option v-for="a in accounts" :key="a.id" :label="a.nickname + ' · ' + a.wechatId" :value="a.wechatId" />
          </el-select>
        </div>
        <div class="mode" :class="{ on: bindMode === 'new' }" @click="selectBindMode('new')">
          <i class="el-icon-full-screen"></i>
          <div><b>扫码新增微信号</b><small>没有合适的微信号？扫码绑定一个新的</small></div>
        </div>
        <div v-if="bindMode === 'new'" class="qr-wrap">
          <img v-if="bindQr" :src="bindQr" alt="" class="qr" />
          <div v-else class="qr qr-empty"><i class="el-icon-loading"></i></div>
          <span :class="{ ok: bindStep === 'done', err: bindStep === 'error' }">{{ bindTip }}</span>
          <el-button size="mini" round :loading="bindBusy" @click.stop="refreshBindQr">刷新二维码</el-button>
        </div>
        <div class="mode" :class="{ on: bindMode === 'skip' }" @click="selectBindMode('skip')">
          <i class="el-icon-time"></i>
          <div><b>暂不绑定</b><small>稍后可在伴侣详情页绑定微信号</small></div>
        </div>
      </div>
      <span slot="footer">
        <el-button round @click="publishVisible = false">关闭</el-button>
        <el-button type="primary" round :loading="bindBusy" @click="finishPublish">完成</el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<script>
import { roleCardApi, companionApi, wechatApi, imageStudioApi, getUser } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "CompanionEditorView",
  components: { DashboardLayout },
  data() {
    return {
      roleCards: [],
      accounts: [],
      editId: null,
      editCardIsSystem: false,
      saving: false,
      relationOptions: ["初识", "逐渐熟悉", "关系稳定", "关系亲近", "深度羁绊"],
      cityOptions: ["北京", "上海", "广州", "深圳", "杭州", "成都", "武汉", "南京", "西安", "重庆", "苏州", "长沙", "天津", "青岛", "厦门", "郑州"],
      traitChips: ["温柔", "治愈", "活泼", "高冷", "理性", "幽默", "粘人", "成熟", "元气", "文艺"],
      welcomeTpls: ["你好呀，今天过得怎么样呢？", "终于等到你啦~", "嗨，我在这里，随时陪你聊天。"],
      promptSectionDefs: [
        { key: "identity", label: "身份与背景", placeholder: "角色身份、成长背景、日常处境", rows: 2 },
        { key: "knowledge_limits", label: "知识与认知边界", placeholder: "哪些事熟悉、哪些不懂，以及不会假装亲历的内容", rows: 2 },
        { key: "personality", label: "性格与情绪底色", placeholder: "性格关键词、情绪稳定性、亲密表达方式", rows: 2 },
        { key: "reaction_tendencies", label: "典型反应倾向", placeholder: "每行可写：倾向：会怎样回应｜触发：什么情境｜抑制：什么情境不这样做", rows: 3 },
        { key: "speaking_style", label: "说话风格", placeholder: "整体语气、常用措辞、面对不同情境的表达方式", rows: 2 },
        { key: "expression_habits", label: "微信表达习惯", placeholder: "逐行写消息节奏、emoji、标点、直接程度、情绪范围或方言；没设定就留空", rows: 3 },
        { key: "relationship_boundary", label: "关系与边界", placeholder: "和用户的关系定位、亲密边界、禁止越界点", rows: 2 },
        { key: "life_routine", label: "生活轨迹", placeholder: "作息、兴趣、今天可能会分享的生活细节", rows: 2 },
        { key: "visual_identity", label: "视觉设定", placeholder: "外貌、穿搭、拍照风格，可与外貌描写互补", rows: 2 },
        { key: "tool_rules", label: "工具使用规则", placeholder: "什么时候发图、发语音、主动联系或记录计划", rows: 2 },
        { key: "memory_rules", label: "记忆规则", placeholder: "哪些信息要记住，哪些只在当前对话里使用", rows: 2 },
        { key: "safety_rules", label: "安全边界", placeholder: "拒绝方式、敏感话题处理、未成年人/隐私边界", rows: 2 },
        { key: "anti_caricature", label: "防止人设表演", placeholder: "这个角色最容易被写成什么夸张套路，哪些特征不能每轮展示", rows: 2 },
        { key: "custom_extra", label: "其他补充", placeholder: "无法归类但需要保留的设定", rows: 3 },
      ],
      // 简易模式引导问题
      rcMode: "simple",
      expanding: false,
      simple: {
        who: "",
        relationship: "",
        style: "",
        examples: "",
        taboos: "",
      },
      // 角色卡（类）表单
      rc: {
        photo: "",
        referenceImage: "",
        grokReference: "",
        safetyStatus: "unknown",
        appearance: "",
        refRules: "",
        negRules: "",
        tagsText: "",
        relationshipMode: "friend",
        attractionStyle: "caring",
        interactionMode: "hybrid",
        location: "北京",
        prompt: "",
        promptSections: {
          identity: "",
          knowledge_limits: "",
          personality: "",
          reaction_tendencies: "",
          speaking_style: "",
          expression_habits: "",
          relationship_boundary: "",
          life_routine: "",
          visual_identity: "",
          tool_rules: "",
          memory_rules: "",
          safety_rules: "",
          anti_caricature: "",
          custom_extra: "",
        },
      },
      // 伴侣（实例）表单
      cp: {
        age: 19,
        identity: "",
        relation: "初识",
        city: "北京",
        personality: "",
        traits: [],
        speakingStyle: "",
        welcome: "",
        customPrompt: "",
        imageMode: "edit_first",
        historyLimit: 12,
        intimacy: 30,
        relationshipMode: "friend",
        attractionStyle: "caring",
        interactionMode: "hybrid",
        deepThinking: true,
      },
      form: { name: "", description: "", roleCardId: "" },
      // 编辑伴侣时存下完整展示资料，保存时全量回填，避免覆盖未在本页编辑的字段
      presentationBase: null,
      // 图像库选择弹窗
      libVisible: false,
      libLoading: false,
      libAssets: [],
      // 生图弹窗
      genVisible: false,
      genMode: "fast",
      genPrompt: "",
      genCount: 1,
      generating: false,
      genResults: [],
      // 发布弹窗
      publishVisible: false,
      bindMode: "exist",
      chosenWx: "",
      bindBusy: false,
      bindJobId: "",
      bindQr: "",
      bindStep: "idle",
      bindError: "",
      bindPollTimer: null,
      initialLoading: true,
    };
  },
  created() {
    this.loadData()
      .catch((e) => this.$message.warning(e.message || "加载编辑器数据失败"))
      .finally(() => {
        this.initialLoading = false;
      });
  },
  beforeDestroy() {
    this.stopBindPolling();
  },
  computed: {
    isRoleCard() {
      return this.$route.path === "/role-card-editor" || this.$route.query.type === "role-card";
    },
    isAdmin() {
      const u = getUser();
      return !!(u && u.is_admin);
    },
    readonly() {
      // 普通用户不能编辑系统角色卡：进入即只读
      return this.isRoleCard && !!this.editId && this.editCardIsSystem && !this.isAdmin;
    },
    previewPhoto() {
      if (this.isRoleCard) return this.rc.photo || require("@/assert/主页人物.png");
      const card = this.roleCards.find((c) => c.id === this.form.roleCardId);
      return (card && card.cover) || require("@/assert/主页人物.png");
    },
    previewSubtitle() {
      if (this.isRoleCard) {
        return this.rc.tagsText || "性格标签会显示在这里";
      }
      const card = this.roleCards.find((c) => c.id === this.form.roleCardId);
      return [card ? "基于 " + card.name : "未选择角色卡", this.cp.age + "岁", this.cp.identity].filter(Boolean).join(" · ");
    },
    canUseReferenceImage() {
      return !!this.rc.referenceImage && ["approved", "allowed"].includes((this.rc.safetyStatus || "").toLowerCase());
    },
    referenceStatusText() {
      const status = (this.rc.safetyStatus || "unknown").toLowerCase();
      return { approved: "已通过", allowed: "已通过", pending: "未审核", rejected: "不可用", blocked: "不可用", unknown: "未知" }[status] || status;
    },
    bindTip() {
      if (this.bindStep === "done") return "扫码成功，已绑定到这个伴侣";
      if (this.bindStep === "conflict") return "扫码授权成功，请确认是否换绑";
      if (this.bindStep === "error") return this.bindError || "二维码生成失败，请刷新重试";
      if (this.bindStep === "starting") return "正在生成二维码…";
      return "请使用微信扫码授权";
    },
  },
  methods: {
    async loadData() {
      const q = this.$route.query;
      try {
        const list = await roleCardApi.list();
        this.roleCards = (list || []).map((rc) => ({
          id: rc.id,
          name: rc.name,
          desc: rc.description,
          cover: rc.reference_image_url || "",
          prompt: rc.prompt,
          promptSections: rc.prompt_sections || {},
          relationshipMode: rc.relationship_mode || "friend",
          attractionStyle: rc.attraction_style || "caring",
          interactionMode: rc.interaction_mode || "hybrid",
          location: rc.location_name || "北京",
          isSystem: !!rc.is_system,
          safetyStatus: rc.reference_image_safety_status || "unknown",
          safetyMessage: rc.reference_image_safety_message || "",
          grokReference: rc.grok_reference_image_url || "",
        }));
      } catch (e) {
        /* ignore */
      }
      if (!this.isRoleCard) {
        try {
          this.accounts = (await wechatApi.list()).map((a) => ({
            id: a.id,
            nickname: a.nickname || a.wechat_account_id,
            wechatId: a.wechat_account_id,
          }));
        } catch (e) {
          this.accounts = [];
        }
      }
      // 预选角色卡（同时把伴侣实际定位默认带入该卡的推荐值）
      if (q.roleCardId) {
        const match = this.roleCards.find((c) => String(c.id) === String(q.roleCardId));
        if (match) this.selectRoleCard(match);
      } else if (!this.isRoleCard && this.roleCards[0]) {
        this.selectRoleCard(this.roleCards[0]);
      }
      // 编辑模式预填
      if (q.id) {
        this.editId = q.id;
        if (this.isRoleCard) {
          this.rcMode = "pro";
          const rc = this.roleCards.find((c) => String(c.id) === String(q.id));
          if (rc) {
            this.form.name = rc.name;
            this.form.description = rc.desc;
            this.rc.prompt = rc.prompt;
            this.applyPromptSections(rc.promptSections, rc.prompt);
            this.rc.photo = rc.cover;
            this.rc.referenceImage = rc.cover;
            this.rc.grokReference = rc.grokReference || "";
            this.rc.relationshipMode = rc.relationshipMode || "friend";
            this.rc.attractionStyle = rc.attractionStyle || "caring";
            this.rc.interactionMode = rc.interactionMode || "hybrid";
            this.rc.location = rc.location || "北京";
            this.rc.safetyStatus = rc.safetyStatus || "unknown";
            this.editCardIsSystem = !!rc.isSystem;
          }
        } else {
          await this.prefillCompanion(q.id);
        }
      } else {
        // 新建模式：若有本地草稿则恢复
        this.restoreDraft();
      }
    },
    selectRoleCard(card) {
      this.form.roleCardId = card.id;
      // 选卡后，伴侣的实际定位默认带入这张卡的推荐值（用户可再改）
      if (!this.editId) {
        this.cp.relationshipMode = card.relationshipMode || "friend";
        this.cp.attractionStyle = card.attractionStyle || "caring";
        this.cp.interactionMode = card.interactionMode || "hybrid";
        this.cp.city = card.location || "北京";
      }
    },
    async prefillCompanion(id) {
      try {
        const c = await companionApi.detail(id);
        this.form.name = c.name;
        this.form.description = c.description;
        this.form.roleCardId = c.role_card_id;
        this.cp.customPrompt = c.custom_prompt || "";
        this.cp.welcome = c.welcome_message || "";
        this.cp.historyLimit = c.message_history_limit || 12;
        this.cp.imageMode = c.image_generation_mode || "edit_first";
        this.cp.intimacy = c.intimacy_base_level !== null && c.intimacy_base_level !== undefined
          ? c.intimacy_base_level
          : 30;
        this.cp.relationshipMode = c.relationship_mode || "friend";
        this.cp.attractionStyle = c.attraction_style || "caring";
        this.cp.interactionMode = c.interaction_mode || "hybrid";
        this.cp.deepThinking = c.deep_thinking !== false;
        const p = c.presentation || {};
        this.presentationBase = p;
        if (p.age_label) this.cp.age = parseInt(p.age_label) || this.cp.age;
        this.cp.identity = p.identity_label || "";
        this.cp.relation = p.relationship_label || this.cp.relation;
        this.cp.city = p.location_name || this.cp.city;
        this.cp.personality = p.personality_label || "";
        this.cp.traits = p.traits || [];
        this.cp.speakingStyle = p.speaking_style || "";
      } catch (e) {
        this.$message.warning("加载伴侣信息失败");
      }
    },
    goBack() {
      this.$router.push(this.isRoleCard ? "/role-cards" : "/companions");
    },
    goRoleCard() {
      this.$router.push("/role-card-editor");
    },
    emptyPromptSections() {
      return this.promptSectionDefs.reduce((acc, item) => {
        acc[item.key] = "";
        return acc;
      }, {});
    },
    applyPromptSections(sections, fallbackPrompt) {
      const next = this.emptyPromptSections();
      Object.keys(next).forEach((key) => {
        next[key] = (sections && sections[key]) || "";
      });
      if (!Object.values(next).some((v) => String(v || "").trim()) && fallbackPrompt) {
        next.custom_extra = fallbackPrompt;
      }
      this.rc.promptSections = next;
    },
    toggleTrait(t) {
      const i = this.cp.traits.indexOf(t);
      if (i > -1) this.cp.traits.splice(i, 1);
      else this.cp.traits.push(t);
    },
    async onUploadReference(file) {
      this.rc.photo = URL.createObjectURL(file);
      try {
        const res = await roleCardApi.uploadReference(file);
        this.rc.referenceImage = res.reference_image_url;
        this.rc.grokReference = res.grok_reference_image_url || "";
        this.rc.safetyStatus = res.safety_status || "unknown";
        this.rc.safetyMessage = res.safety_message || "";
        if (res.safety_status === "pending") {
          this.$message.info("参考图已上传，后台审核通过后才能用于生成");
        } else if (res.safety_status === "blocked" || res.safety_status === "rejected") {
          this.$message.warning("参考图安全检测未通过：" + (res.safety_message || ""));
        } else {
          this.$message.success("参考图已上传");
        }
      } catch (e) {
        this.$message.error(e.message || "上传失败");
      }
      return false;
    },
    async openLibrary() {
      this.libVisible = true;
      this.libLoading = true;
      try {
        this.libAssets = (await imageStudioApi.listAssets()) || [];
      } catch (e) {
        this.$message.error(e.message || "加载图像库失败");
      } finally {
        this.libLoading = false;
      }
    },
    pickFromLibrary(asset) {
      this.rc.photo = asset.public_url;
      this.rc.referenceImage = asset.public_url;
      this.rc.grokReference = "";
      // 平台生成/编辑的图视为已审核，用户上传的图仍需后台审核
      this.rc.safetyStatus = asset.kind === "uploaded" ? "pending" : "approved";
      this.rc.safetyMessage = "";
      this.libVisible = false;
      if (this.rc.safetyStatus === "pending") {
        this.$message.info("已选用，上传图片需后台审核通过后才能用于生成");
      } else {
        this.$message.success("已选用图像库图片作为参考图");
      }
    },
    openGen(mode) {
      if (mode === "edit" && !this.canUseReferenceImage) {
        this.$message.warning("参考图未审核通过，暂时不能用于生成");
        return;
      }
      this.genMode = mode;
      this.genResults = [];
      this.genPrompt = [this.rc.appearance, this.rc.refRules].filter(Boolean).join("，") || "清纯温柔的少女，自然光，半身像";
      this.genVisible = true;
    },
    async runGenerate() {
      if (!this.genPrompt.trim()) {
        this.$message.warning("请填写画面提示词");
        return;
      }
      this.generating = true;
      try {
        const res = await roleCardApi.generatePhoto({
          prompt: this.genPrompt.trim(),
          mode: this.genMode === "edit" ? "edit" : "fast_first",
          count: this.genCount,
          reference_image_url: this.canUseReferenceImage ? this.rc.referenceImage : "",
          grok_reference_image_url: this.canUseReferenceImage ? this.rc.grokReference : "",
        });
        this.genResults = (res.images || []).map((im) => im.url);
        if (res.grok_reference_image_url && !this.rc.grokReference) {
          this.rc.grokReference = res.grok_reference_image_url;
        }
        if (!this.genResults.length) this.$message.warning("未返回图片");
        else this.$message.success("已生成 " + this.genResults.length + " 张");
      } catch (e) {
        this.$message.error(e.message || "生成失败");
      } finally {
        this.generating = false;
      }
    },
    useGenerated(url) {
      this.rc.photo = url;
      if (!this.rc.referenceImage) this.rc.referenceImage = url;
      this.rc.safetyStatus = "approved";
      this.genVisible = false;
      this.$message.success("已设为角色照片");
    },
    draftKey() {
      return this.isRoleCard ? "xyo_draft_rolecard" : "xyo_draft_companion";
    },
    saveDraft() {
      const snapshot = { form: this.form, rc: this.rc, cp: this.cp, simple: this.simple, rcMode: this.rcMode };
      try {
        localStorage.setItem(this.draftKey(), JSON.stringify(snapshot));
        this.$message.success("已存为草稿");
      } catch (e) {
        this.$message.error("草稿保存失败");
      }
    },
    restoreDraft() {
      try {
        const raw = localStorage.getItem(this.draftKey());
        if (!raw) return;
        const d = JSON.parse(raw);
        if (d.form) Object.assign(this.form, d.form);
        if (d.rc) Object.assign(this.rc, d.rc);
        if (d.cp) Object.assign(this.cp, d.cp);
        if (d.simple) Object.assign(this.simple, d.simple);
        if (d.rcMode) this.rcMode = d.rcMode;
        this.$message.info("已恢复上次的草稿");
      } catch (e) {
        /* ignore */
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey());
      } catch (e) {
        /* ignore */
      }
    },
    async runExpandPersona() {
      if (!(this.simple.who.trim() || this.form.description.trim())) {
        this.$message.warning("先填写「她是谁」或上面的一句话介绍");
        return;
      }
      this.expanding = true;
      try {
        const res = await roleCardApi.expandPersona({
          name: this.form.name || "",
          summary: this.form.description || "",
          who: this.simple.who,
          relationship: this.simple.relationship,
          style: this.simple.style,
          examples: this.simple.examples,
          taboos: this.simple.taboos,
        });
        const sections = (res && res.sections) || {};
        Object.keys(this.rc.promptSections).forEach((key) => {
          if (sections[key]) this.$set(this.rc.promptSections, key, sections[key]);
        });
        this.rcMode = "pro";
        this.$message.success("人设已生成 ✓ 已切到专业模式，可逐项微调后保存");
      } catch (e) {
        this.$message.error(e.message || "AI 扩写失败，请稍后重试");
      } finally {
        this.expanding = false;
      }
    },
    mergedPromptSections() {
      const sections = { ...this.rc.promptSections };
      const visualParts = [sections.visual_identity];
      if (this.rc.appearance && !String(sections.visual_identity || "").includes(this.rc.appearance)) {
        visualParts.push("外貌：" + this.rc.appearance);
      }
      if (this.rc.refRules) visualParts.push("生图正向规则：" + this.rc.refRules);
      if (this.rc.negRules) visualParts.push("生图负面规则：" + this.rc.negRules);
      sections.visual_identity = visualParts.filter(Boolean).join("\n");
      return sections;
    },
    buildRoleCardPrompt() {
      const parts = [this.promptSectionDefs.map((item) => this.rc.promptSections[item.key]).filter(Boolean).join("\n")];
      if (this.rc.appearance) parts.push("【外貌】" + this.rc.appearance);
      if (this.rc.refRules) parts.push("【生图参考规则】" + this.rc.refRules);
      if (this.rc.negRules) parts.push("【生图负面规则】" + this.rc.negRules);
      return parts.filter(Boolean).join("\n");
    },
    async onPublish() {
      if (!this.form.name) {
        this.$message.warning("请填写名称");
        return;
      }
      if (this.saving) return;
      if (this.isRoleCard) {
        if (!Object.values(this.rc.promptSections).some((v) => String(v || "").trim())) {
          this.$message.warning("请填写人格 Prompt");
          return;
        }
        this.saving = true;
        const payload = {
          name: this.form.name,
          description: this.form.description,
          prompt: this.buildRoleCardPrompt(),
          prompt_sections: this.mergedPromptSections(),
          reference_image_url: this.rc.referenceImage || "",
          grok_reference_image_url: this.rc.grokReference || "",
          reference_image_safety_status: this.rc.safetyStatus || "unknown",
          reference_image_safety_message: this.rc.safetyMessage || "",
          relationship_mode: this.rc.relationshipMode || "friend",
          attraction_style: this.rc.attractionStyle || "caring",
          interaction_mode: this.rc.interactionMode || "hybrid",
          location_name: this.rc.location || "北京",
        };
        try {
          if (this.editId) await roleCardApi.update(this.editId, payload);
          else await roleCardApi.create(payload);
          this.clearDraft();
          this.$message.success("角色卡已保存");
          this.$router.push("/role-cards");
        } catch (e) {
          this.$message.error(e.message || "保存失败");
        } finally {
          this.saving = false;
        }
      } else {
        if (!this.form.roleCardId) {
          this.$message.warning("请选择一张角色卡");
          return;
        }
        this.saving = true;
        const payload = {
          name: this.form.name,
          description: this.form.description,
          role_card_id: this.form.roleCardId,
          custom_prompt: this.cp.customPrompt || "",
          welcome_message: this.cp.welcome || "",
          message_history_limit: this.cp.historyLimit,
          image_generation_mode: this.cp.imageMode,
          intimacy_base_level: this.cp.intimacy,
          relationship_mode: this.cp.relationshipMode || "friend",
          attraction_style: this.cp.attractionStyle || "caring",
          interaction_mode: this.cp.interactionMode || "hybrid",
          deep_thinking: this.cp.deepThinking !== false,
        };
        try {
          if (this.editId) {
            await companionApi.update(this.editId, payload);
            await this.savePresentation(this.editId, this.presentationBase);
            this.$message.success("伴侣已更新");
            this.$router.push("/companions");
          } else {
            const created = await companionApi.create(payload);
            this.clearDraft();
            this.editId = created.id;
            await this.savePresentation(created.id, null);
            this.bindMode = this.accounts.length ? "exist" : "new";
            if (this.bindMode === "new") this.refreshBindQr();
            this.publishVisible = true;
          }
        } catch (e) {
          this.$message.error(e.message || "保存失败");
        } finally {
          this.saving = false;
        }
      }
    },
    presentationPayloadFromForm(base) {
      // 以已有展示资料为底，仅覆盖本页编辑的字段；base 为空（新建）时各字段回落后端默认
      const b = base || {};
      const has = (v) => v !== undefined && v !== null;
      return {
        age: Number(this.cp.age) || 0,
        gender: b.gender || "",
        identity: this.cp.identity || "",
        relationship_label: this.cp.relation || b.relationship_label || "",
        school_year: b.school_year || "",
        major: b.major || "",
        birthday: b.birthday || "",
        zodiac: b.zodiac || "",
        personality_label: this.cp.personality || "",
        speaking_style: this.cp.speakingStyle || "",
        values_text: b.values_text || "",
        background_story: b.background_story || "",
        behavior_rules: b.behavior_rules || "",
        active_hours: b.active_hours || "10:00 - 22:00",
        location_name: this.cp.city || b.location_name || "",
        location_timezone: b.location_timezone || "",
        location_latitude: b.location_latitude || "",
        location_longitude: b.location_longitude || "",
        sync_to_web: has(b.sync_to_web) ? b.sync_to_web : true,
        web_to_weixin: has(b.web_to_weixin) ? b.web_to_weixin : true,
        allow_proactive: has(b.allow_proactive) ? b.allow_proactive : true,
        traits: Array.isArray(this.cp.traits) ? this.cp.traits : [],
        emotion_tags: Array.isArray(b.emotion_tags) ? b.emotion_tags : [],
        story_tags: Array.isArray(b.story_tags) ? b.story_tags : [],
      };
    },
    async savePresentation(companionId, base) {
      // best-effort：展示资料失败不阻断主流程（伴侣已创建/更新）
      try {
        await companionApi.updatePresentation(companionId, this.presentationPayloadFromForm(base));
      } catch (e) {
        this.$message.warning("展示资料保存失败，可稍后在伴侣详情页补充");
      }
    },
    selectBindMode(mode) {
      this.bindMode = mode;
      if (mode === "new" && !this.bindJobId) this.refreshBindQr();
    },
    async refreshBindQr() {
      if (!this.editId || this.bindBusy) return;
      this.stopBindPolling();
      this.bindBusy = true;
      this.bindStep = "starting";
      this.bindQr = "";
      this.bindJobId = "";
      this.bindError = "";
      try {
        const job = await companionApi.createWechatBindJob(this.editId);
        this.applyBindJob(job);
        if (!["done", "error"].includes(this.bindStep)) this.startBindPolling();
      } catch (e) {
        this.bindStep = "error";
        this.bindError = e.message || "二维码生成失败";
      } finally {
        this.bindBusy = false;
      }
    },
    applyBindJob(job) {
      if (!job) return;
      this.bindJobId = job.id || this.bindJobId;
      this.bindQr = job.qr_data_url || job.qr_url || this.bindQr;
      if (job.status === "bound") {
        this.bindStep = "done";
        this.chosenWx = job.wechat_account_id || this.chosenWx;
        this.stopBindPolling();
        this.loadAccounts();
      } else if (job.status === "conflict") {
        this.stopBindPolling();
        this.bindStep = "conflict";
        this.bindError = "";
        this.confirmBindReplace(job);
      } else if (["failed", "expired", "already_connected"].includes(job.status)) {
        this.bindStep = "error";
        this.bindError = job.error_message || "绑定失败，请刷新二维码重试";
        this.stopBindPolling();
      } else {
        this.bindStep = "waiting";
      }
    },
    async confirmBindReplace(job) {
      if (this.bindBusy) return;
      this.bindBusy = true;
      try {
        await this.$confirm(
          job.error_message || "该微信号已绑定到其他账号。继续操作将解除原账号中的微信号及伴侣关联。",
          "确认换绑微信号",
          {
            type: "warning",
            confirmButtonText: "确认换绑",
            cancelButtonText: "取消",
            distinguishCancelAndClose: true,
          }
        );
      } catch (e) {
        this.bindStep = "error";
        this.bindError = "已取消换绑，原账号的绑定保持不变";
        this.bindBusy = false;
        return;
      }
      try {
        const updated = await wechatApi.confirmBindReplace(job.id, job.wechat_account_id);
        this.applyBindJob(updated);
      } catch (e) {
        this.bindStep = "error";
        this.bindError = e.message || "换绑失败，请刷新二维码重试";
      } finally {
        this.bindBusy = false;
      }
    },
    startBindPolling() {
      this.stopBindPolling();
      this.bindPollTimer = window.setInterval(async () => {
        if (!this.bindJobId) return;
        try {
          const job = await wechatApi.getBindJob(this.bindJobId);
          this.applyBindJob(job);
        } catch (e) {
          this.bindStep = "error";
          this.bindError = e.message || "查询扫码状态失败";
          this.stopBindPolling();
        }
      }, 2500);
    },
    stopBindPolling() {
      if (this.bindPollTimer) {
        window.clearInterval(this.bindPollTimer);
        this.bindPollTimer = null;
      }
    },
    async loadAccounts() {
      try {
        this.accounts = (await wechatApi.list()).map((a) => ({
          id: a.id,
          nickname: a.nickname || a.wechat_account_id,
          wechatId: a.wechat_account_id,
        }));
      } catch (e) {
        this.accounts = [];
      }
    },
    async finishPublish() {
      if (this.bindBusy) return;
      this.bindBusy = true;
      try {
        if (this.bindMode === "exist") {
          if (!this.chosenWx) {
            this.$message.warning("请选择要绑定的微信号");
            return;
          }
          await companionApi.bindWechat(this.editId, this.chosenWx, true);
          this.$message.success("已绑定所选微信号");
        } else if (this.bindMode === "new") {
          if (this.bindStep !== "done") {
            this.$message.warning("请先完成扫码授权，或选择暂不绑定");
            return;
          }
          this.$message.success("新微信号已绑定到这个伴侣");
        } else {
          this.$message.success("已创建，稍后可绑定微信号");
        }
        this.publishVisible = false;
        this.stopBindPolling();
        this.$router.push("/companions");
      } catch (e) {
        this.$message.error(e.message || "绑定失败");
      } finally {
        this.bindBusy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.editor-head {
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    font-size: 18px;
    color: #999;
    cursor: pointer;
  }

  b {
    display: block;
    font-size: 16px;
  }

  small {
    color: #bbb;
    font-size: 12px;
  }
}

.editor-page {
  .concept-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    padding: 14px 18px;
    border-radius: 14px;
    background: linear-gradient(90deg, #fff4ee, #fff);
    border: 1px solid #edcbb8;

    &.companion {
      background: linear-gradient(90deg, #eef4ff, #fff);
      border-color: #dbe6ff;

      i {
        color: #6b8cff;
      }
    }

    i {
      font-size: 24px;
      color: #c94225;
    }

    p {
      margin: 0;
      color: #666;
      font-size: 13px;
      line-height: 1.6;

      b {
        color: #c94225;
      }
    }
  }

  .top-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 16px;

    .el-button--primary {
      border: 0;
      background: linear-gradient(90deg, #dc5c3e, #c94225);
    }
  }

  .bottom-actions {
    position: sticky;
    bottom: 12px;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 16px;
    padding: 12px 18px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.08);

    .ba-hint {
      color: #aaa;
      font-size: 13px;
    }

    .ba-btns {
      display: flex;
      gap: 8px;
    }

    .el-button--primary {
      border: 0;
      background: linear-gradient(90deg, #dc5c3e, #c94225);
    }
  }

  .layout {
    display: flex;
    gap: 20px;

    .form-col {
      flex: 1;
      min-width: 0;

      .section {
        margin-bottom: 12px;
        padding: 14px 18px;
        border-radius: 16px;
        background: #fff;

        // 表单紧凑：收紧 el-form-item 的纵向间距和 top-label 占高
        ::v-deep .el-form-item {
          margin-bottom: 12px;
        }
        ::v-deep .el-form-item__label {
          padding: 0 0 2px;
          line-height: 1.5;
          font-size: 13px;
          color: #666;
        }

        h3 {
          display: flex;
          align-items: center;
          margin: 0 0 12px;
          font-size: 15px;

          span {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            margin-right: 10px;
            border-radius: 50%;
            color: #fff;
            font-size: 12px;
            background: #c94225;
          }

          .step-optional {
            margin-left: 8px;
            padding: 1px 8px;
            border-radius: 10px;
            color: #b0b0b0;
            font-size: 11px;
            font-weight: 400;
            background: #f3f3f5;
          }
        }

        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 16px;
        }

        .mode-switch-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .mode-tip {
          margin: 8px 0 0;
          color: #aaa;
          font-size: 12px;
        }

        .chip {
          display: inline-block;
          margin: 0 8px 8px 0;
          padding: 5px 14px;
          border: 1px solid #e8e3ed;
          border-radius: 16px;
          color: #888;
          font-size: 13px;
          cursor: pointer;
          transition: 0.2s;

          &:hover {
            border-color: #d8d1e0;
          }

          &.on {
            color: #fff;
            border-color: transparent;
            background: linear-gradient(90deg, #dc5c3e, #c94225);
          }
        }
      }

      .photo-block {
        .photo-preview {
          width: 150px;
          height: 190px;
          margin-bottom: 14px;
          border-radius: 14px;
          overflow: hidden;
          background: linear-gradient(180deg, #f7e7da, #e5c3ad);

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center top;
          }

          .photo-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #7255e9;

            i {
              font-size: 34px;
            }

            span {
              margin-top: 6px;
              font-size: 12px;
            }
          }
        }

        .photo-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 10px;
        }

        .photo-tip {
          margin: 0;
          color: #bbb;
          font-size: 12px;
        }
      }

      .rc-picker {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        margin-bottom: 12px;

        .rc-option {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          border: 1px solid #e8e3ed;
          border-radius: 12px;
          cursor: pointer;
          transition: 0.2s;

          img {
            width: 44px;
            height: 44px;
            border-radius: 10px;
            object-fit: cover;
            object-position: center top;
            background: #f7e7da;
          }

          b {
            font-size: 14px;
          }

          p {
            margin: 2px 0 0;
            color: #999;
            font-size: 12px;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .el-icon-success {
            position: absolute;
            right: 10px;
            top: 10px;
            color: #c94225;
          }

          &:hover {
            border-color: #d8d1e0;
          }

          &.on {
            border-color: #c94225;
            background: #fff7ef;
          }
        }
      }

      .new-rc {
        color: #c94225;
        border-color: #d8d1e0;
      }

      .welcome-tpls {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        span {
          padding: 5px 12px;
          border-radius: 14px;
          color: #b64d38;
          font-size: 12px;
          cursor: pointer;
          background: #fff4ee;
        }
      }
    }

    .preview-col {
      width: 320px;
      flex-shrink: 0;

      .preview-card,
      .hint-card {
        margin-bottom: 16px;
        padding: 20px;
        border-radius: 16px;
        background: #fff;

        h3 {
          margin: 0 0 14px;
          font-size: 15px;
        }
      }

      .cover {
        width: 100%;
        height: auto;
        max-height: 420px;
        margin-bottom: 12px;
        border-radius: 14px;
        object-fit: contain;
        object-position: center top;
        background: linear-gradient(180deg, #f7e7da, #e5c3ad);
      }

      h2 {
        margin: 0 0 4px;
        font-size: 20px;
      }

      > .preview-card > p {
        margin: 0 0 8px;
        color: #999;
        font-size: 12px;
      }

      .bio {
        line-height: 1.5;
      }

      .appearance-preview {
        margin-top: 10px;
        padding: 10px;
        border-radius: 10px;
        background: #fafafa;

        label {
          display: block;
          margin-bottom: 4px;
          color: #999;
          font-size: 11px;
        }

        span {
          color: #666;
          font-size: 12px;
          line-height: 1.5;
        }
      }

      .bars {
        margin-top: 8px;

        label {
          display: block;
          margin-bottom: 4px;
          color: #999;
          font-size: 11px;
        }
      }

      .hint-card {
        background: linear-gradient(145deg, #fff7ef, #fff);

        p {
          margin: 0;
          color: #666;
          font-size: 13px;
          line-height: 1.7;

          b {
            color: #c94225;
          }
        }
      }
    }
  }
}

.gen-results {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 8px;

  .gen-item {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;

    img {
      width: 100%;
      height: 90px;
      object-fit: cover;
      object-position: center top;
    }

    .use {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 3px 0;
      text-align: center;
      color: #fff;
      font-size: 11px;
      background: rgba(201, 66, 37, 0.86);
    }
  }
}

.gen-note {
  margin: 12px 0 0;
  color: #aaa;
  font-size: 12px;

  i {
    margin-right: 4px;
  }
}

.lib-tip {
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 10px;
  color: #b64d38;
  font-size: 12px;
  line-height: 1.5;
  background: #fff7ef;
}

.lib-empty {
  padding: 40px 0;
  text-align: center;
  color: #bbb;
  font-size: 13px;
}

.lib-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;

  .lib-item {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;

    img {
      display: block;
      width: 100%;
      height: 120px;
      object-fit: cover;
      object-position: center top;
    }

    .pick {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 3px 0;
      text-align: center;
      color: #fff;
      font-size: 11px;
      background: rgba(201, 66, 37, 0.86);
      opacity: 0;
      transition: 0.2s;
    }

    &:hover .pick {
      opacity: 1;
    }
  }
}

.pub-ok {
  margin: 0 0 16px;
  color: #52c41a;
  font-size: 14px;

  i {
    margin-right: 4px;
  }
}

.bind-modes {
  .mode {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    padding: 14px;
    border: 1px solid #e8e3ed;
    border-radius: 14px;
    cursor: pointer;
    transition: 0.2s;

    > i {
      font-size: 22px;
      color: #7255e9;
    }

    b {
      display: block;
      font-size: 14px;
    }

    small {
      color: #aaa;
      font-size: 12px;
    }

    &:hover {
      border-color: #d8d1e0;
    }

    &.on {
      border-color: #c94225;
      background: #fff7ef;
    }
  }

  .select-wrap {
    margin: -4px 0 10px;
    padding: 0 4px;
  }

  .qr-wrap {
    margin: -4px 0 10px;
    text-align: center;

    .qr {
      width: 130px;
      height: 130px;
      border-radius: 12px;
      object-fit: contain;
      background: #f6efe6;
    }

    span {
      display: block;
      margin-top: 8px;
      color: #999;
      font-size: 12px;

      &.ok {
        color: #52c41a;
      }

      &.err {
        color: #f5222d;
      }
    }

    .qr-empty {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #7255e9;
      font-size: 28px;
    }
  }
}

@media (max-width: 768px) {
  .editor-head {
    flex-wrap: wrap;
    gap: 10px;
  }

  .gen-results,
  .lib-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .editor-page {
    .concept-bar {
      flex-wrap: wrap;
    }

    .top-actions {
      flex-wrap: wrap;
    }

    .layout {
      flex-direction: column;

      .form-col .section {
        padding: 14px 16px;

        .grid-2 {
          grid-template-columns: 1fr;
        }
      }

      .preview-col {
        width: 100%;
      }
    }
  }
}
</style>

<style scoped lang="scss">
/* Warm operational workspace overrides. Business markup and bindings intentionally stay untouched. */
.editor-head {
  gap: 12px;
  color: var(--xyo-ink);

  > i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid var(--xyo-line);
    border-radius: var(--xyo-radius-sm);
    color: var(--xyo-muted);
    background: var(--xyo-surface-raised);
    transition: color 180ms ease, border-color 180ms ease, background-color 180ms ease;

    &:hover {
      border-color: var(--xyo-line-strong);
      color: var(--xyo-primary);
      background: var(--xyo-accent-pale);
    }
  }

  b {
    color: var(--xyo-ink-strong);
    font-size: 15px;
    letter-spacing: -0.01em;
  }

  small {
    color: var(--xyo-muted);
  }
}

.editor-page {
  color: var(--xyo-ink);

  .concept-bar,
  .concept-bar.companion {
    gap: 14px;
    margin: 6px 0 18px;
    padding: 14px 16px;
    border: 1px solid var(--xyo-line);
    border-left: 4px solid var(--xyo-primary);
    border-radius: var(--xyo-radius-md);
    background: var(--xyo-surface-muted);

    > i,
    p b {
      color: var(--xyo-primary);
    }

    > i {
      font-size: 21px;
    }

    p {
      color: var(--xyo-ink-soft);
      line-height: 1.65;
    }
  }

  .top-actions {
    align-items: center;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--xyo-line);

    .el-button--primary {
      border-color: var(--xyo-primary) !important;
      background: var(--xyo-primary) !important;
    }

    .readonly-tip {
      color: var(--xyo-muted);
      font-size: 12px;
    }
  }

  .layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(288px, 336px);
    align-items: start;
    gap: 24px;

    .form-col {
      min-width: 0;
      overflow: hidden;
      border: 1px solid var(--xyo-line);
      border-radius: var(--xyo-radius-lg);
      background: var(--xyo-surface-raised);
      box-shadow: var(--xyo-shadow-sm);

      .section {
        margin: 0;
        padding: 26px clamp(20px, 3vw, 34px) 22px;
        border: 0;
        border-bottom: 1px solid var(--xyo-line);
        border-radius: 0;
        background: transparent;

        &:last-child {
          border-bottom: 0;
        }

        ::v-deep .el-form-item {
          margin-bottom: 18px;
        }

        ::v-deep .el-form-item__label {
          padding-bottom: 7px !important;
          color: var(--xyo-ink-soft);
          font-weight: 650;
        }

        h3 {
          margin-bottom: 20px;
          color: var(--xyo-ink-strong);
          font-size: 16px;
          font-weight: 720;
          letter-spacing: -0.015em;

          > span {
            width: 24px;
            height: 24px;
            margin-right: 11px;
            border: 1px solid #d78968;
            color: var(--xyo-primary);
            background: var(--xyo-accent-pale);
          }

          .step-optional {
            border: 1px solid var(--xyo-line);
            color: var(--xyo-muted);
            background: var(--xyo-surface-muted);
          }
        }

        .grid-2 {
          gap: 0 20px;
        }

        .mode-switch-row {
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px dashed var(--xyo-line);
        }

        .mode-tip {
          color: var(--xyo-muted);
          line-height: 1.55;
        }

        .chip {
          margin: 0 7px 8px 0;
          padding: 6px 12px;
          border-color: var(--xyo-line-strong);
          border-radius: 999px;
          color: var(--xyo-ink-soft);
          background: var(--xyo-surface-raised);

          &:hover {
            border-color: #c88869;
            color: var(--xyo-primary);
            background: var(--xyo-accent-pale);
          }

          &.on {
            border-color: var(--xyo-primary);
            color: #fff;
            background: var(--xyo-primary);
          }
        }
      }

      .photo-block {
        display: grid;
        grid-template-columns: 150px minmax(0, 1fr);
        align-items: start;
        gap: 20px;

        .photo-preview {
          grid-row: 1 / span 2;
          margin: 0;
          border: 1px solid var(--xyo-line);
          background: var(--xyo-surface-muted);

          .photo-empty {
            color: var(--xyo-primary);
          }
        }

        .photo-actions {
          align-content: flex-start;
          margin: 0;
        }

        .photo-tip {
          color: var(--xyo-muted);
          line-height: 1.6;
        }
      }

      .rc-picker {
        gap: 12px;

        .rc-option {
          min-width: 0;
          padding: 12px;
          border-color: var(--xyo-line);
          color: var(--xyo-ink);
          background: var(--xyo-surface-raised);

          img {
            border: 1px solid var(--xyo-line);
            background: var(--xyo-surface-muted);
          }

          p {
            color: var(--xyo-muted);
          }

          .el-icon-success {
            color: var(--xyo-primary);
          }

          &:hover {
            border-color: #c88869;
            background: var(--xyo-accent-pale);
          }

          &.on {
            border-color: var(--xyo-primary);
            background: var(--xyo-accent-pale);
            box-shadow: inset 3px 0 0 var(--xyo-primary);
          }
        }
      }

      .new-rc {
        border-color: var(--xyo-line-strong) !important;
        color: var(--xyo-primary) !important;
        background: var(--xyo-accent-pale) !important;
      }

      .welcome-tpls span {
        border: 1px solid var(--xyo-line);
        border-radius: 999px;
        color: var(--xyo-primary);
        background: var(--xyo-accent-pale);
        transition: border-color 180ms ease, background-color 180ms ease;

        &:hover {
          border-color: #c88869;
          background: var(--xyo-accent-soft);
        }
      }
    }

    .preview-col {
      position: sticky;
      top: 72px;
      width: auto;
      min-width: 0;

      .preview-card,
      .hint-card {
        margin-bottom: 14px;
        padding: 20px;
        border: 1px solid var(--xyo-line);
        border-radius: var(--xyo-radius-lg);
        background: var(--xyo-surface-raised);
        box-shadow: var(--xyo-shadow-sm);

        h3 {
          color: var(--xyo-ink-strong);
          font-size: 13px;
          letter-spacing: 0.04em;
        }
      }

      .cover {
        max-height: min(42vh, 420px);
        border: 1px solid var(--xyo-line);
        border-radius: var(--xyo-radius-md);
        background: var(--xyo-surface-muted);
      }

      h2 {
        color: var(--xyo-ink-strong);
        letter-spacing: -0.025em;
      }

      > .preview-card > p,
      .bars label,
      .appearance-preview label {
        color: var(--xyo-muted);
      }

      .appearance-preview {
        border: 1px solid var(--xyo-line);
        color: var(--xyo-ink-soft);
        background: var(--xyo-surface-muted);

        span {
          color: var(--xyo-ink-soft);
        }
      }

      .hint-card {
        border-left: 3px solid var(--xyo-primary);
        background: var(--xyo-accent-pale);

        p {
          color: var(--xyo-ink-soft);

          b {
            color: var(--xyo-primary);
          }
        }
      }
    }
  }

  .bottom-actions {
    bottom: max(12px, env(safe-area-inset-bottom));
    margin-top: 18px;
    padding: 12px 16px;
    border: 1px solid var(--xyo-line-strong);
    border-radius: var(--xyo-radius-md);
    background: rgba(252, 251, 253, 0.94);
    box-shadow: 0 12px 34px rgba(34, 28, 48, 0.14);

    .ba-hint {
      color: var(--xyo-muted);
    }

    .el-button--primary {
      border-color: var(--xyo-primary) !important;
      background: var(--xyo-primary) !important;
    }
  }
}

.gen-results .gen-item,
.lib-grid .lib-item {
  border: 1px solid var(--xyo-line);
  background: var(--xyo-surface-muted);

  .use,
  .pick {
    color: #fff;
    background: rgba(201, 66, 37, 0.92);
  }
}

.gen-note,
.lib-empty {
  color: var(--xyo-muted);
}

.lib-tip {
  border: 1px solid var(--xyo-line);
  color: var(--xyo-ink-soft);
  background: var(--xyo-accent-pale);
}

.pub-ok {
  color: var(--xyo-success);
}

.bind-modes {
  .mode {
    border-color: var(--xyo-line);
    color: var(--xyo-ink);
    background: var(--xyo-surface-raised);

    > i {
      color: var(--xyo-primary);
    }

    small {
      color: var(--xyo-muted);
    }

    &:hover {
      border-color: #c88869;
      background: var(--xyo-accent-pale);
    }

    &.on {
      border-color: var(--xyo-primary);
      background: var(--xyo-accent-pale);
      box-shadow: inset 3px 0 0 var(--xyo-primary);
    }
  }

  .qr-wrap {
    .qr {
      border: 1px solid var(--xyo-line);
      background: var(--xyo-surface-muted);
    }

    span {
      color: var(--xyo-muted);

      &.ok {
        color: var(--xyo-success);
      }

      &.err {
        color: var(--xyo-danger);
      }
    }

    .qr-empty {
      color: var(--xyo-primary);
    }
  }
}

@media (max-width: 980px) {
  .editor-page .layout {
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .editor-head {
    flex-wrap: nowrap;

    small {
      display: none;
    }
  }

  .editor-page {
    .concept-bar,
    .concept-bar.companion {
      align-items: flex-start;
      flex-wrap: nowrap;
      margin-bottom: 14px;
      padding: 12px 13px;
    }

    .top-actions {
      justify-content: flex-start;
      overflow-x: auto;
      flex-wrap: nowrap;
      padding-bottom: 13px;

      > * {
        flex: 0 0 auto;
      }
    }

    .layout {
      display: block;

      .form-col {
        border-radius: var(--xyo-radius-md);

        .section {
          padding: 22px 16px 18px;

          .grid-2,
          .rc-picker {
            grid-template-columns: minmax(0, 1fr);
          }

          .mode-switch-row {
            align-items: flex-start;
            flex-direction: column;
          }
        }

        .photo-block {
          grid-template-columns: 112px minmax(0, 1fr);
          gap: 14px;

          .photo-preview {
            width: 112px;
            height: 148px;
          }

          .photo-actions {
            display: grid;
            grid-template-columns: minmax(0, 1fr);

            ::v-deep .el-upload,
            ::v-deep .el-button {
              width: 100%;
            }
          }

          .photo-tip {
            grid-column: 1 / -1;
          }
        }
      }

      .preview-col {
        position: static;
        width: 100%;
        margin-top: 16px;

        .cover {
          max-height: 360px;
        }
      }
    }

    .bottom-actions {
      align-items: stretch;
      flex-direction: column;
      bottom: calc(66px + env(safe-area-inset-bottom));
      margin-right: -4px;
      margin-left: -4px;

      .ba-hint {
        font-size: 12px;
      }

      .ba-btns {
        display: grid;
        grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);

        .el-button {
          width: 100%;
          margin: 0;
        }
      }
    }
  }

  .gen-results,
  .lib-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 420px) {
  .editor-page .layout .form-col .photo-block {
    display: block;

    .photo-preview {
      width: 100%;
      height: 220px;
      margin-bottom: 14px;
    }

    .photo-actions {
      margin-bottom: 10px;
    }
  }
}
</style>
